import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { FaTrash, FaPlus, FaForward, FaDolly } from "react-icons/fa";
import api from "../../../libs/axios";
import { useAuth } from "../../../provider/auth.provider";
import { useConfirm } from "../../../provider/confirm.provider";
import MaterialModeBadge from "../components/MaterialModeBadge";
import { SelectWithSearch, SelectOption } from "../../../utils/select-with-search";
import UnitSelect from "../components/UnitSelect";

// Material Issue Note statuses — "received" is the site-side delivery.
const REQUEST_STATUS_BADGE: Record<string, string> = {
  requested: "bg-yellow-100 text-yellow-800",
  approved: "bg-indigo-100 text-indigo-800",
  issued: "bg-blue-100 text-blue-800",
  received: "bg-green-100 text-green-800",
  rejected: "bg-red-100 text-red-800",
  cancelled: "bg-red-100 text-red-800",
};

// Mirrors the source list and status colours used by the Visits tab.
const MATERIAL_SOURCE_LABELS: Record<string, string> = {
  espl_warehouse: "ESPL Warehouse",
  staff_inventory: "Staff Inventory",
  client_material: "Client Material",
  direct_purchase: "Direct Purchase",
};

const VISIT_STATUS_BADGE: Record<string, string> = {
  completed: "bg-green-100 text-green-800",
  in_progress: "bg-blue-100 text-blue-800",
  cancelled: "bg-red-100 text-red-800",
};

interface MaterialsTabProps {
  siteId: string;
  // Lets the parent refresh the site header/stepper after the workflow moves on.
  onWorkflowChange?: () => void;
  // True once billing has been finalized. Material planning on a locked site
  // is read-only — the API refuses every write with 423.
  locked?: boolean;
  // The site, for its material_mode (with / without material).
  site?: any;
}

const MaterialsTab: React.FC<MaterialsTabProps> = ({ siteId, onWorkflowChange, locked = false, site }) => {
  const { hasPermission } = useAuth();
  const { prompt, confirm } = useConfirm();
  // One confirm for every row delete on this tab.
  const removeRow = async (url: string, what: string) => {
    const ok = await confirm({ title: `Remove ${what}?`, message: `The ${what} is deleted from this site.`, confirmText: "Remove", variant: "danger" });
    if (!ok) return;
    try {
      await api.delete(url);
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || `Failed to remove ${what}`);
    }
  };
  const materialMode = site?.material_mode as "with_material" | "without_material" | null | undefined;
  const modeBy = site?.material_mode_user
    ? `${site.material_mode_user.first_name || ""} ${site.material_mode_user.last_name || ""}`.trim()
    : "";
  // Viewing the planned material survives the lock; changing it does not.
  const canViewPlanning = hasPermission("IncSite", "material_planning");
  const canPlan = canViewPlanning && !locked;
  const canRequestMaterial = hasPermission("IncMaterialRequest", "create") && !locked;
  // "Continue without material" marks four workflow stages complete in one
  // go, so it is gated separately from ordinary planning.
  const canSkipPlanning =
    hasPermission("IncSite", "material_planning_skip") && !locked && materialMode !== "without_material";

  const canViewVisits = hasPermission("IncSite", "visits");

  const [requests, setRequests] = useState<any[]>([]);
  const [requesting, setRequesting] = useState(false);
  const [planning, setPlanning] = useState<any[]>([]);
  const [clientMaterials, setClientMaterials] = useState<any[]>([]);
  const [clientEquipment, setClientEquipment] = useState<any[]>([]);
  // Installed material, grouped by the visit it was recorded against.
  const [installedVisits, setInstalledVisits] = useState<any[]>([]);
  const [installedTotals, setInstalledTotals] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // planning form
  const [item, setItem] = useState<SelectOption | null>(null);
  const [reqQty, setReqQty] = useState("");
  const [whQty, setWhQty] = useState("");
  const [staffQty, setStaffQty] = useState("");
  const [procQty, setProcQty] = useState("");

  // client material form
  const [cmName, setCmName] = useState("");
  const [cmQty, setCmQty] = useState("");
  const [cmUnit, setCmUnit] = useState("");
  // client equipment form
  const [ceName, setCeName] = useState("");
  const [ceSerial, setCeSerial] = useState("");
  const [ceQty, setCeQty] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, cm, ce, installed, mr] = await Promise.all([
        api.get(`/inc/sites/${siteId}/material-planning`),
        api.get(`/inc/sites/${siteId}/client-materials`),
        api.get(`/inc/sites/${siteId}/client-equipment`),
        // Visits are permission-gated separately — skip the call rather than
        // letting a 403 blow up the whole tab.
        canViewVisits
          ? api.get(`/inc/sites/${siteId}/installed-materials`)
          : Promise.resolve({ data: { data: { visits: [], totals: [] } } }),
        api.get(`/inc/sites/${siteId}/material-requests`).catch(() => ({ data: { data: [] } })),
      ]);
      setPlanning(p.data?.data || []);
      setClientMaterials(cm.data?.data || []);
      setClientEquipment(ce.data?.data || []);
      setInstalledVisits(installed.data?.data?.visits || []);
      setInstalledTotals(installed.data?.data?.totals || []);
      // Site requests are Material Issue Notes — the list is paginated, so the
      // rows sit one level deeper than the other endpoints here.
      const mrRows = mr.data?.data?.data ?? mr.data?.data;
      setRequests(Array.isArray(mrRows) ? mrRows : []);
    } finally {
      setLoading(false);
    }
  }, [siteId, canViewVisits]);

  useEffect(() => {
    load();
  }, [load]);

  const addPlanning = async () => {
    if (!item) {
      toast.error("Select a material item");
      return;
    }
    await api.post(`/inc/sites/${siteId}/material-planning`, {
      material_item_id: item.id,
      required_quantity: Number(reqQty) || 0,
      warehouse_quantity: Number(whQty) || 0,
      staff_quantity: Number(staffQty) || 0,
      procurement_quantity: Number(procQty) || 0,
    });
    setItem(null);
    setReqQty("");
    setWhQty("");
    setStaffQty("");
    setProcQty("");
    load();
  };

  // Material planning is optional — a site with nothing to install can move
  // straight on to field work. The server marks all four material stages
  // complete in one go, since none of them can ever happen for this site.
  const skipPlanning = async () => {
    const remarks = await prompt({
      title: "Continue without material?",
      message:
        "The site is marked as going ahead WITHOUT material: Material Planned, Requested, Issued and Delivered are all completed at once and field work is unlocked.",
      label: "Reason (optional)",
      placeholder: "e.g. Client supplies everything / survey-only work",
      multiline: true,
      maxLength: 500,
      confirmText: "Continue without material",
      cancelText: "Go back",
      variant: "warning",
    });
    if (remarks === null) return;
    try {
      await api.post(`/inc/sites/${siteId}/material-planning/skip`, remarks.trim() ? { remarks: remarks.trim() } : {});
      toast.success("Continuing without material — you can start field work");
      onWorkflowChange?.();
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to continue without material");
    }
  };

  // Raises a request for every planned line. Material Management picks it up
  // from there (issue → deliver), and each step advances this site's workflow.
  const requestMaterial = async () => {
    if (planning.length === 0) {
      toast.error("Plan material for this site first");
      return;
    }
    setRequesting(true);
    try {
      const res = await api.post(`/inc/sites/${siteId}/material-requests`, {});
      toast.success(`Material request ${res.data?.data?.issue_number || ""} raised`);
      onWorkflowChange?.();
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to raise material request");
    } finally {
      setRequesting(false);
    }
  };

  const addClientMaterial = async () => {
    if (!cmName) return toast.error("Item name required");
    await api.post(`/inc/sites/${siteId}/client-materials`, { item_name: cmName, quantity: Number(cmQty) || 0, unit: cmUnit });
    setCmName("");
    setCmQty("");
    setCmUnit("");
    load();
  };

  const addClientEquipment = async () => {
    if (!ceName) return toast.error("Equipment name required");
    await api.post(`/inc/sites/${siteId}/client-equipment`, { equipment_name: ceName, serial_number: ceSerial, quantity: Number(ceQty) || 1 });
    setCeName("");
    setCeSerial("");
    setCeQty("");
    load();
  };

  const numInput = "w-24 px-2 py-1.5 text-sm border border-gray-300 rounded";
  const txtInput = "px-2 py-1.5 text-sm border border-gray-300 rounded";

  if (loading) return <p className="text-sm text-gray-500">Loading...</p>;

  return (
    <div className="space-y-6">
      {locked && (
        <p className="text-xs text-purple-800 bg-purple-50 border border-purple-200 rounded px-3 py-2 mb-3">
          Billing has been finalized for this site — it is locked and this tab is read-only.
        </p>
      )}
      {/* Which way the site went — the one fact the timeline alone cannot tell. */}
      {materialMode === "without_material" ? (
        <div className="flex items-start gap-3 rounded border border-orange-200 bg-orange-50 px-4 py-3">
          <MaterialModeBadge mode={materialMode} />
          <div className="text-sm text-orange-900">
            <p className="font-medium">This site is continuing without material.</p>
            <p className="text-xs text-orange-800 mt-0.5">
              {site?.material_mode_at ? `Chosen ${new Date(site.material_mode_at).toLocaleString()}` : "Recorded"}
              {modeBy ? ` by ${modeBy}` : ""}.
              {site?.material_mode_remarks ? ` Reason: ${site.material_mode_remarks}` : ""}
            </p>
            {canPlan && (
              <p className="text-xs text-orange-700 mt-1">Adding a planned material line below switches the site to “With material”.</p>
            )}
          </div>
        </div>
      ) : materialMode === "with_material" ? (
        <div className="flex items-center gap-3 rounded border border-teal-200 bg-teal-50 px-4 py-2">
          <MaterialModeBadge mode={materialMode} />
          <span className="text-xs text-teal-800">
            This site goes ahead with planned material
            {site?.material_mode_at ? ` (since ${new Date(site.material_mode_at).toLocaleDateString()}${modeBy ? `, ${modeBy}` : ""})` : ""}.
          </span>
        </div>
      ) : (
        <div className="flex items-center gap-3 rounded border border-gray-200 bg-gray-50 px-4 py-2">
          <MaterialModeBadge mode={materialMode} showUndecided />
          <span className="text-xs text-gray-600">
            Plan material below, or use “Continue without material” if this site needs none.
          </span>
        </div>
      )}

      {/* Material planning */}
      <section>
        <h3 className="text-sm font-semibold text-gray-700 mb-2">Material Planning</h3>
        <p className="text-xs text-gray-400 mb-2">Availability is read live from Material Management stock.</p>
        {canPlan && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-3 flex flex-wrap gap-2 items-end">
            <div className="min-w-[220px] flex-1">
              <label className="block text-xs text-gray-500 mb-1">Item</label>
              <SelectWithSearch value={item} onChange={setItem} apiEndpoint="/inc/material-items" dataKey="items" labelKey="item_name" displayFormat={(i) => `${i.item_code} — ${i.item_name}`} placeholder="Search items..." minSearchLength={0} />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Required</label>
              <input value={reqQty} onChange={(e) => setReqQty(e.target.value)} className={numInput} />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Warehouse</label>
              <input value={whQty} onChange={(e) => setWhQty(e.target.value)} className={numInput} />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Staff</label>
              <input value={staffQty} onChange={(e) => setStaffQty(e.target.value)} className={numInput} />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Procure</label>
              <input value={procQty} onChange={(e) => setProcQty(e.target.value)} className={numInput} />
            </div>
            <button onClick={addPlanning} className="inline-flex items-center gap-1 px-3 py-2 text-sm text-white bg-blue-800 rounded-md hover:bg-blue-900">
              <FaPlus className="w-3 h-3" /> Add
            </button>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          {canRequestMaterial && (
            <button
              onClick={requestMaterial}
              disabled={requesting || planning.length === 0}
              title={planning.length === 0 ? "Add planned material first" : "Send this site's planned material to Material Management"}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-blue-800 rounded-md hover:bg-blue-900 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              <FaDolly className="w-3 h-3" /> {requesting ? "Requesting..." : "Request Material"}
            </button>
          )}
          {canSkipPlanning && (
            <>
              <button onClick={skipPlanning} className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                <FaForward className="w-3 h-3" /> Continue without material
              </button>
              <span className="text-xs text-gray-400">
                Marks Planned, Requested, Issued and Delivered complete and unlocks field work.
              </span>
            </>
          )}
        </div>
        {planning.length === 0 ? (
          <p className="text-sm text-gray-500">No material planned.</p>
        ) : (
          <table className="min-w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="px-3 py-2 text-left">Item</th>
                <th className="px-3 py-2 text-right">Required</th>
                <th className="px-3 py-2 text-right">Warehouse</th>
                <th className="px-3 py-2 text-right">Staff</th>
                <th className="px-3 py-2 text-right">Procure</th>
                <th className="px-3 py-2 text-right">Available</th>
                {canPlan && <th className="px-3 py-2"></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {planning.map((r) => (
                <tr key={r.id}>
                  <td className="px-3 py-2">{r.material_item ? `${r.material_item.item_code} — ${r.material_item.item_name}` : `#${r.material_item_id}`}</td>
                  <td className="px-3 py-2 text-right">{r.required_quantity}</td>
                  <td className="px-3 py-2 text-right">{r.warehouse_quantity}</td>
                  <td className="px-3 py-2 text-right">{r.staff_quantity}</td>
                  <td className="px-3 py-2 text-right">{r.procurement_quantity}</td>
                  <td className="px-3 py-2 text-right font-medium text-green-700">{r.available_quantity ?? "-"}</td>
                  {canPlan && (
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => removeRow(`/inc/material-planning/${r.id}`, "planned material line")} title="Remove" className="p-1.5 text-red-500 hover:bg-red-50 rounded">
                        <FaTrash className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* Material requests raised for this site */}
      <section>
        <h3 className="text-sm font-semibold text-gray-700 mb-2">Material Requests</h3>
        <p className="text-xs text-gray-400 mb-2">
          Fulfilled in Material Management — issuing and delivering there advances this site's workflow.
        </p>
        {requests.length === 0 ? (
          <p className="text-sm text-gray-500">No material requested yet.</p>
        ) : (
          <div className="space-y-3">
            {requests.map((r) => (
              <div key={r.id} className="border border-gray-200 rounded-lg p-3">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <div className="text-sm font-medium text-gray-800">
                    {r.issue_number}
                    <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${REQUEST_STATUS_BADGE[r.status] || "bg-gray-100 text-gray-700"}`}>
                      {r.status}
                    </span>
                  </div>
                  <div className="text-xs text-gray-400">
                    {r.requester ? `${r.requester.first_name || ""} ${r.requester.last_name || ""}`.trim() : ""}
                    {r.requested_at ? ` · ${new Date(r.requested_at).toLocaleDateString()}` : ""}
                    {r.issued_at ? ` · issued ${new Date(r.issued_at).toLocaleDateString()}` : ""}
                    {r.received_at ? ` · delivered ${new Date(r.received_at).toLocaleDateString()}` : ""}
                  </div>
                </div>
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                    <tr>
                      <th className="px-3 py-2 text-left">Item</th>
                      <th className="px-3 py-2 text-right">Requested</th>
                      <th className="px-3 py-2 text-right">Issued</th>
                      <th className="px-3 py-2 text-right">Delivered</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {(r.items || []).map((it: any) => (
                      <tr key={it.id}>
                        <td className="px-3 py-2">
                          {it.item ? `${it.item.item_code} — ${it.item.item_name}` : `#${it.item_id}`}
                        </td>
                        <td className="px-3 py-2 text-right">{it.requested_quantity}</td>
                        <td className="px-3 py-2 text-right">{it.issued_quantity ?? 0}</td>
                        <td className="px-3 py-2 text-right">{it.received_quantity ?? 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Installed material, visit by visit */}
      {canViewVisits && (
        <section>
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Installed Materials (Visit-wise)</h3>
          <p className="text-xs text-gray-400 mb-2">Recorded against each visit in the Visits tab.</p>
          {installedVisits.every((v) => (v.materials || []).length === 0) ? (
            <p className="text-sm text-gray-500">No material installed yet.</p>
          ) : (
            <div className="space-y-3">
              {installedVisits
                .filter((v) => (v.materials || []).length > 0)
                .map((v) => {
                  const visitTotal = (v.materials || []).reduce(
                    (sum: number, m: any) => sum + Number(m.quantity || 0),
                    0,
                  );
                  return (
                    <div key={v.id} className="border border-gray-200 rounded-lg p-3">
                      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                        <div className="text-sm font-medium text-gray-800">
                          Visit #{v.visit_number}
                          <span className="ml-2 text-xs text-gray-500">
                            {v.visit_date ? new Date(v.visit_date).toLocaleDateString() : "No date"}
                          </span>
                          <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${VISIT_STATUS_BADGE[v.visit_status] || "bg-gray-100 text-gray-700"}`}>
                            {v.visit_status}
                          </span>
                        </div>
                        <div className="text-xs text-gray-400">
                          {v.creator ? `${v.creator.first_name || ""} ${v.creator.last_name || ""}`.trim() : `#${v.created_by}`}
                          {" · "}{(v.materials || []).length} item(s) · qty {visitTotal}
                        </div>
                      </div>
                      <table className="min-w-full text-sm">
                        <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                          <tr>
                            <th className="px-3 py-2 text-left">Item</th>
                            <th className="px-3 py-2 text-right">Qty</th>
                            <th className="px-3 py-2 text-left">Unit</th>
                            <th className="px-3 py-2 text-left">Source</th>
                            <th className="px-3 py-2 text-left">Remarks</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {(v.materials || []).map((m: any) => (
                            <tr key={m.id}>
                              <td className="px-3 py-2">{m.item ? `${m.item.item_code} — ${m.item.item_name}` : `#${m.inventory_item_id}`}</td>
                              <td className="px-3 py-2 text-right">{m.quantity}</td>
                              <td className="px-3 py-2 text-gray-600">{m.unit || "-"}</td>
                              <td className="px-3 py-2 text-gray-500 text-xs">{MATERIAL_SOURCE_LABELS[m.source_type] || m.source_type}</td>
                              <td className="px-3 py-2 text-gray-600 break-words">{m.remarks || "-"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  );
                })}

              {installedTotals.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-gray-600 mb-1">Site total by item (all visits)</h4>
                  <table className="min-w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                    <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                      <tr>
                        <th className="px-3 py-2 text-left">Item</th>
                        <th className="px-3 py-2 text-right">Total Qty</th>
                        <th className="px-3 py-2 text-left">Unit</th>
                        <th className="px-3 py-2 text-right">Visits</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {installedTotals.map((t) => (
                        <tr key={t.inventory_item_id}>
                          <td className="px-3 py-2">{t.item_code ? `${t.item_code} — ${t.item_name}` : `#${t.inventory_item_id}`}</td>
                          <td className="px-3 py-2 text-right font-medium">{t.quantity}</td>
                          <td className="px-3 py-2 text-gray-600">{t.unit || "-"}</td>
                          <td className="px-3 py-2 text-right text-gray-600">{t.visit_count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </section>
      )}

      {/* Client materials */}
      <section>
        <h3 className="text-sm font-semibold text-gray-700 mb-2">Client-Supplied Materials</h3>
        {canPlan && (
          <div className="flex flex-wrap gap-2 items-end mb-2">
            <input placeholder="Item name" value={cmName} onChange={(e) => setCmName(e.target.value)} className={txtInput} />
            <input placeholder="Qty" value={cmQty} onChange={(e) => setCmQty(e.target.value)} className={numInput} />
            <UnitSelect value={cmUnit} onChange={setCmUnit} className="w-32" />
            <button onClick={addClientMaterial} className="px-3 py-2 text-sm text-white bg-blue-800 rounded-md hover:bg-blue-900">Add</button>
          </div>
        )}
        {clientMaterials.length === 0 ? (
          <p className="text-sm text-gray-500">None.</p>
        ) : (
          <ul className="text-sm divide-y divide-gray-100 border border-gray-200 rounded-lg">
            {clientMaterials.map((m) => (
              <li key={m.id} className="px-3 py-2 flex justify-between items-center">
                <span>{m.item_name} — {m.quantity} {m.unit}</span>
                {canPlan && <button onClick={() => removeRow(`/inc/client-materials/${m.id}`, "client material")} title="Remove" className="text-red-500"><FaTrash className="w-3.5 h-3.5" /></button>}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Client equipment */}
      <section>
        <h3 className="text-sm font-semibold text-gray-700 mb-2">Client-Supplied Equipment</h3>
        {canPlan && (
          <div className="flex flex-wrap gap-2 items-end mb-2">
            <input placeholder="Equipment name" value={ceName} onChange={(e) => setCeName(e.target.value)} className={txtInput} />
            <input placeholder="Serial" value={ceSerial} onChange={(e) => setCeSerial(e.target.value)} className={txtInput} />
            <input placeholder="Qty" value={ceQty} onChange={(e) => setCeQty(e.target.value)} className={numInput} />
            <button onClick={addClientEquipment} className="px-3 py-2 text-sm text-white bg-blue-800 rounded-md hover:bg-blue-900">Add</button>
          </div>
        )}
        {clientEquipment.length === 0 ? (
          <p className="text-sm text-gray-500">None.</p>
        ) : (
          <ul className="text-sm divide-y divide-gray-100 border border-gray-200 rounded-lg">
            {clientEquipment.map((e) => (
              <li key={e.id} className="px-3 py-2 flex justify-between items-center">
                <span>{e.equipment_name} {e.serial_number ? `(${e.serial_number})` : ""} — {e.quantity}</span>
                {canPlan && <button onClick={() => removeRow(`/inc/client-equipment/${e.id}`, "client equipment")} title="Remove" className="text-red-500"><FaTrash className="w-3.5 h-3.5" /></button>}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};

export default MaterialsTab;